import { AuthDataPayload } from '@api';
import { queryString } from '@helpers';
import { useThemeStore } from '@terminal/components/theme-controller/stores/useThemeStore';
import { useLegalDocuments } from '@terminal/hooks/use-legal-documents/useLegalDocuments';
import { useRemoteStorage } from '@terminal/hooks/use-remote-storage';
import { appPaths, authPaths } from '@terminal/routes/app-paths';
import { queryKeys } from '@terminal/routes/query-params';
import { queryClient } from '@terminal/services/query-client/queryClient';
import { Tracking } from '@terminal/services/tracking';
import { usePostHog as usCustomPostHog } from '@terminal/services/tracking/posthog/usePostHog';
import { usePromoCodeStore } from '@terminal/stores/use-promo-code/usePromoCodeStore';
import { useSubscription } from '@terminal/stores/use-subscription/useSubscription';
import { useUser } from '@terminal/stores/use-user/useUser';
import { ApiFetchResponse } from '@terminal/utils/api-fetch/apiFetch';
import { httpCode } from '@terminal/utils/api-fetch/httpCode';
import { resetStores } from '@terminal/utils/store/createStore';
import {
  getAuthToken,
  getRefreshToken,
  postLogin,
  postLogout,
  postRegister,
} from '@terminal/views/auth/services/auth-services';
import { useNewsSearchStore } from '@terminal/views/news/stores/use-news-search-store/useNewsSearchStore';
import { useOnboardingStore } from '@terminal/views/onboarding/hooks/useOnboardingStore';
import { useScreenerStore } from '@terminal/views/screener/use-screener-store/useScreenerStore';
import { differenceInHours } from 'date-fns';
import { usePostHog as usePostHogBase } from 'posthog-js/react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router';
import { useFlags } from '../use-flags/useFlags';
import { useWatchlist } from '../use-watchlist/useWatchlist';
import { useAuthStore } from './store/authStore';

export interface SignUpProps {
  email: string;
  password: string;
  isProfessional: boolean;
  referralCode?: string;
  promoCode?: string;
  termsAccepted?: string[];
}

export interface LogoutOptions {
  noQuery?: boolean;
  queryParams?: Record<string, unknown>;
}

interface UseAuth {
  email?: string;
  initStores: (session: AuthDataPayload) => Promise<void>;
  signUp: (user: SignUpProps) => Promise<ApiFetchResponse<AuthDataPayload>>;
  logout: (options?: LogoutOptions) => Promise<void>;
  authProcessing: boolean;
  setAuthProcessing: (authProcessing: boolean) => void;
  unLockAccount: () => void;
  isLocked: boolean;
  isAuthenticated: boolean;
  isInitError: boolean;
  isInitSignUp: boolean;
}

const dueToExpire = (timestamp: Date): boolean => {
  const HOURS_BEFORE_RENEW = 48;
  return differenceInHours(timestamp, new Date()) < HOURS_BEFORE_RENEW;
};

export const getSession = async (): Promise<AuthDataPayload | undefined> => {
  const session = await getAuthToken();

  if (session.error) {
    return;
  }

  if (dueToExpire(new Date(session.data.expiry))) {
    const newSession = await getRefreshToken(session.data.token);

    if (!newSession.error) {
      return newSession.data;
    }
  }

  return session.data;
};

export const useAuth = (): UseAuth => {
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation(['news']);

  const { identifyUser, captureUserProperties } = usCustomPostHog();

  const {
    email,
    resetAuthStore,
    updateAuthStore,
    authProcessing,
    setAuthProcessing,
    unLockAccount,
    isLocked,
    isAuthenticated,
    isInitError,
    isInitSignUp,
    setIsInitError,
  } = useAuthStore(state => ({
    email: state.email,
    unLockAccount: state.unlock,
    resetAuthStore: state.destroy,
    updateAuthStore: state.update,
    isLocked: state.isLocked,
    authProcessing: state.authProcessing,
    setAuthProcessing: state.setAuthProcessing,
    isAuthenticated: !!state.token,
    isInitError: state.isInitError,
    isInitSignUp: state.isInitSignUp,
    setIsInitError: state.setIsInitError,
  }));
  const posthogClient = usePostHogBase();

  const initializeUser = useUser(state => state.initialize);
  const initializeSubscription = useSubscription(state => state.initialize);
  const initializeRemoteStorage = useRemoteStorage(state => state.initialize);

  const initializePromoCodeStore = usePromoCodeStore(state => state.initialize);
  const initWatchlist = useWatchlist(state => state.getLists);
  const initScreenerFiltersStore = useScreenerStore(state => state.initStore);
  const initNewsSearchStore = useNewsSearchStore(state => state.initStore);
  const initThemeStore = useThemeStore(state => state.initStore);
  const initFlagsStore = useFlags(state => state.initialise);
  const initOnboardingStore = useOnboardingStore(state => state.initStore);

  const getDocsPendingConsent = useLegalDocuments(
    state => state.getDocsPendingConsent
  );

  const initStores = async (
    session: AuthDataPayload,
    isInitSignUp?: boolean
  ) => {
    identifyUser(session.user_id, session.email);
    Tracking.initialiseUser(session.user_id);

    setAuthProcessing(true);
    updateAuthStore(session, !!isInitSignUp);
    const userResponse = await initializeUser(captureUserProperties);
    const responses = await Promise.all([
      initializeRemoteStorage(),
      initializeSubscription(captureUserProperties),
      initializePromoCodeStore(),
      getDocsPendingConsent(),
    ]);
    await initFlagsStore(posthogClient);
    await initOnboardingStore(userResponse.data);
    await initWatchlist();
    await initScreenerFiltersStore();
    await initNewsSearchStore(t);
    initThemeStore();

    const truthyResponses = [userResponse, ...responses].filter(Boolean);

    const unHandledError = (r: (typeof truthyResponses)[0]) =>
      r?.error && r.error.status !== httpCode.locked;
    const isResponsesError = truthyResponses.some(unHandledError);

    if (isResponsesError) {
      setIsInitError(true);
      navigate(appPaths.error);
    }

    setAuthProcessing(false);
    void Promise.allSettled([
      import(
        '@terminal/views/scenario/scenario-initialization/scenarioInitialization.utils'
      ).then(({ prefetchFallbackScenarioData }) =>
        prefetchFallbackScenarioData()
      ),
      import(
        '@terminal/views/turbo-chart/hooks/use-chart-load/useChartLoad.utils'
      ).then(({ prefetchFallbackChartData }) => prefetchFallbackChartData()),
      import(
        '@terminal/views/screener/screener-initialization/prefetchFallbackScreenerData'
      ).then(({ prefetchFallbackScreenerData }) =>
        prefetchFallbackScreenerData()
      ),
    ]);
  };

  const signUp = async (userDetails: SignUpProps) => {
    const registered = await postRegister(userDetails);

    if (registered.error) {
      return registered;
    }

    const session = await postLogin({
      username: userDetails.email,
      password: userDetails.password,
    });

    if (session.error) {
      return session;
    }

    await initStores(session.data, true);
    return session;
  };

  const logout = async ({ noQuery, queryParams }: LogoutOptions = {}) => {
    resetStores();
    resetAuthStore();
    posthogClient.reset();
    queryClient.clear();

    const clearRedirects = [appPaths.alfred].some(
      path => path === location.pathname
    );

    let queryStr = '';

    if (!noQuery) {
      if (queryParams) {
        queryStr = queryString(queryParams);
      } else if (!clearRedirects) {
        queryStr = queryString({
          [queryKeys.redirect]: `${location.pathname}${location.search}`,
        });
      }
    }

    navigate(authPaths.login + queryStr);
    await postLogout();
  };

  return {
    email,
    isInitError,
    isInitSignUp,
    isAuthenticated,
    isLocked,
    unLockAccount,
    signUp,
    logout,
    initStores,
    authProcessing,
    setAuthProcessing,
  };
};
